import { Dispatch } from 'react';
import { queryClient } from 'src/query-client';
import produce from 'immer';
import {
  addAssetToStack,
  changeAssetOrder,
  changeFolderOrder,
  createAssetFolder,
  deleteAsset,
  moveAsset,
  removeAssetVersionFromStack,
  renameAsset,
  renameFolder,
  restoreDeletedAsset,
  setAssetFolderVisibility,
  setFavoriteAsset
} from '@api/Asset';
import {
  AssetControllerAddAssetToStackParams,
  AssetControllerDeleteAssetParams,
  AssetControllerListAssetsParams,
  AssetControllerRemoveAssetVersionFromStackParams,
  AssetItemDto,
  CreateFolderDto,
  DeleteTranscriptionDto,
  MoveAssetToFolderDto,
  RenameFolderDto,
  RestoreAssetDto,
  SetAssetFavoriteDto,
  SetFolderVisibilityDto,
  TranscribeAssetDto
} from '@api/Api';
import {
  IMediaViewerAction,
  MediaViewerActionTypes
} from '@redux/types/mediaViewerType';
import {
  getItemPlaceholderFromListQuery,
  optimisticListItemRemove,
  optimisticListItemReorder,
  optimisticListItemUpdate
} from '@hooks/queries/helpers';
import { ReorderEvent } from '@components/DraggableList/DraggableList';
import { apiClient } from '@api/client';

export const deleteAssetAction =
  (params: AssetControllerDeleteAssetParams) => async () => {
    await queryClient.cancelQueries({ queryKey: ['assets'] });
    for (const id of params.assetId ?? []) {
      optimisticListItemRemove<AssetItemDto>({
        queryKey: ['assets', 'list'],
        id
      });
    }
    await deleteAsset(params);
    queryClient.invalidateQueries({ queryKey: ['assets'] });
  };

export const restoreDeletedAssetAction =
  (params: RestoreAssetDto) => async () => {
    await queryClient.cancelQueries({ queryKey: ['assets'] });
    for (const id of [params.assetId ?? []].flat()) {
      optimisticListItemRemove<AssetItemDto>({
        queryKey: ['assets', 'list'],
        id
      });
    }
    await restoreDeletedAsset(params);
    queryClient.invalidateQueries({ queryKey: ['assets'] });
  };

export const setFavoriteAssetAction =
  (params: SetAssetFavoriteDto, needRemoveFromList: boolean) => async () => {
    await queryClient.cancelQueries({ queryKey: ['assets'] });
    optimisticListItemUpdate<AssetItemDto>(
      {
        queryKey: ['assets', 'list'],
        id: params.assetId
      },
      (draft) => {
        draft.isFavorite = params.isFavorite;
      }
    );
    await setFavoriteAsset(params);
    queryClient.invalidateQueries({ queryKey: ['assets'] });
  };

export const renameAssetAction =
  ({
    name,
    assetId,
    assetVersionId
  }: {
    name: string;
    assetId: string;
    assetVersionId: string;
  }) =>
  async (dispatch: Dispatch<IMediaViewerAction>) => {
    await renameAsset({ assetVersionId, name });
    await queryClient.invalidateQueries({ queryKey: ['assets'] });
    dispatch({
      type: MediaViewerActionTypes.RENAME_ASSET_VERSION,
      payload: { name, assetVersionId }
    });
  };

export const setFolderAccessability =
  (data: SetFolderVisibilityDto) => async () => {
    await setAssetFolderVisibility(data);
    await queryClient.invalidateQueries({ queryKey: ['assets'] });
  };

export const renameFolderAction = (params: RenameFolderDto) => async () => {
  await renameFolder(params);
  await queryClient.invalidateQueries({ queryKey: ['assets'] });
};

export const createFolderAction = (params: CreateFolderDto) => async () => {
  const {
    data: { asset }
  } = await createAssetFolder(params);
  await queryClient.invalidateQueries({ queryKey: ['assets'] });
  await queryClient.invalidateQueries({ queryKey: ['campaigns', 'list'] });
  await queryClient.invalidateQueries({ queryKey: ['folders', 'simple'] });
  return asset;
};

export const moveAssetToFolder = (params: MoveAssetToFolderDto) => async () => {
  await queryClient.cancelQueries({ queryKey: ['assets'] });
  for (const id of [params.assetId ?? []].flat()) {
    optimisticListItemRemove<AssetItemDto>({
      queryKey: ['assets', 'list'],
      id
    });
  }
  await moveAsset(params);
  queryClient.invalidateQueries({ queryKey: ['assets'] });
};

export const reorganizeAssetList = (e: ReorderEvent) => async () => {
  const cancelPromise = queryClient.cancelQueries({ queryKey: ['assets'] });
  const beforeAssetId = optimisticListItemReorder<AssetItemDto>(
    {
      queryKey: ['assets', 'list'],
      id: e.item.id,
      predicate: (query) => {
        const listParams = query.queryKey[2] as AssetControllerListAssetsParams;
        const customOrder = listParams.orderBy?.some((x: any) =>
          x.startsWith('order:')
        );
        return !!customOrder;
      }
    },
    e.prevIndex,
    e.newIndex
  );
  await cancelPromise;
  if (beforeAssetId !== false) {
    await changeAssetOrder({
      id: e.item.id,
      beforeAssetId
    });
  }
  queryClient.invalidateQueries({ queryKey: ['assets'] });
};

export const reorganizeFolderList = (e: ReorderEvent) => async () => {
  const cancelPromise = queryClient.cancelQueries({ queryKey: ['assets'] });
  const beforeFolderId = optimisticListItemReorder<AssetItemDto>(
    {
      queryKey: ['assets', 'list'],
      id: e.item.id,
      predicate: (query) => {
        const listParams = query.queryKey[2] as AssetControllerListAssetsParams;
        const customOrder = listParams.orderBy?.some((x: any) =>
          x.startsWith('order:')
        );
        return !!customOrder;
      }
    },
    e.prevIndex,
    e.newIndex
  );
  await cancelPromise;
  if (beforeFolderId !== false) {
    await changeFolderOrder({
      id: e.item.id,
      beforeFolderId
    });
  }
  queryClient.invalidateQueries({ queryKey: ['assets'] });
};

export const addAssetToStackAction =
  ({ assetId, toAssetId }: AssetControllerAddAssetToStackParams) =>
  async () => {
    const cancelPromise = queryClient.cancelQueries({ queryKey: ['assets'] });
    const asset = getItemPlaceholderFromListQuery<AssetItemDto>(
      ['assets', 'list'],
      assetId
    );
    optimisticListItemRemove<AssetItemDto>({
      queryKey: ['assets', 'list'],
      id: assetId
    });
    if (asset?.asset) {
      const { versionsCount, versions } = asset.asset;
      const optimisticUpdate = (draft: AssetItemDto) => {
        if (!draft?.asset) return;
        draft.asset.versionsCount += versionsCount;
        draft.asset.versions.unshift(...versions);
        (draft as any).versionsOptimisticUpdate = true;
      };
      optimisticListItemUpdate<AssetItemDto>(
        {
          queryKey: ['assets', 'list'],
          id: toAssetId
        },
        optimisticUpdate
      );
      queryClient.setQueriesData(
        { queryKey: ['assets', 'item', toAssetId] },
        (asset) => {
          if (!asset) return asset;
          return produce(asset, optimisticUpdate);
        }
      );
    }
    await cancelPromise;
    await addAssetToStack({ assetId, toAssetId });
    queryClient.invalidateQueries({ queryKey: ['assets'] });
  };

export const removeAssetVersionFromStackAction =
  ({
    assetId,
    assetVersionId
  }: AssetControllerRemoveAssetVersionFromStackParams) =>
  async () => {
    await queryClient.cancelQueries({ queryKey: ['assets'] });
    const optimisticUpdate = (draft: AssetItemDto) => {
      if (!draft.asset?.versions) return;
      const hasVersion = draft.asset.versions.some(
        (v) => v.id === assetVersionId
      );
      if (!hasVersion) return;
      draft.asset.versions = draft.asset.versions.filter(
        (v) => v.id !== assetVersionId
      );
      draft.asset.versionsCount -= 1;
      (draft as any).versionsOptimisticUpdate = true;
    };
    optimisticListItemUpdate<AssetItemDto>(
      {
        queryKey: ['assets', 'list'],
        id: assetId
      },
      optimisticUpdate
    );
    queryClient.setQueriesData(
      { queryKey: ['assets', 'item', assetId] },
      (asset) => {
        if (!asset) return asset;
        return produce(asset, optimisticUpdate);
      }
    );
    const {
      data: { newAsset }
    } = await removeAssetVersionFromStack({ assetId, assetVersionId });
    queryClient.invalidateQueries({ queryKey: ['assets'] });
    return newAsset;
  };

export const transcribeAssetVersion =
  (params: TranscribeAssetDto & { assetId: string }) => async () => {
    const optimisticUpdate = (draft: AssetItemDto) => {
      const version = draft?.asset?.versions.find(
        (v) => v.id === params.assetVersionId
      );
      if (!version) return;
      const { metadata } = version;
      if (metadata.type !== 'audio' && metadata.type !== 'video') return;
      if (metadata.transcription.status !== 'skipped') return;
      metadata.transcription.status = 'pending';
    };
    optimisticListItemUpdate<AssetItemDto>(
      {
        queryKey: ['assets', 'list'],
        id: params.assetId
      },
      optimisticUpdate
    );
    queryClient.setQueriesData(
      { queryKey: ['assets', 'item', params?.assetId] },
      (asset) => {
        if (!asset) return asset;
        return produce(asset, optimisticUpdate);
      }
    );
    await apiClient.asset.assetControllerTranscribe(params);
  };

export const deleteTranscriptionAssetVersion =
  (params: DeleteTranscriptionDto & { assetId: string }) => async () => {
    const optimisticUpdate = (draft: AssetItemDto) => {
      const version = draft?.asset?.versions.find(
        (v) => v.id === params.assetVersionId
      );
      if (!version) return;
      const { metadata } = version;
      if (metadata.type !== 'audio' && metadata.type !== 'video') return;
      if (metadata.transcription.status !== 'ready') return;
      metadata.transcription.status = 'skipped';
    };
    optimisticListItemUpdate<AssetItemDto>(
      {
        queryKey: ['assets', 'list'],
        id: params.assetId
      },
      optimisticUpdate
    );
    queryClient.setQueriesData(
      { queryKey: ['assets', 'item', params?.assetId] },
      (asset) => {
        if (!asset) return asset;
        return produce(asset, optimisticUpdate);
      }
    );
    await apiClient.asset.assetControllerDeleteTranscription(params);
  };
